/*
  ActionRegistry — central registry for AI-first actions discoverable by the Command Palette.
  - Components/adapters can register actions on mount and unregister on unmount
  - Emits lightweight events for analytics/agents
*/

export type ActionCategory = 'Global' | 'Workspace' | 'Entity' | 'Selection' | 'AI';

export interface RegisteredActionContext {
  entity?: string;
  selection?: unknown;
  route?: string;
}

export interface RegisteredAction {
  id: string;
  label: string;
  category?: ActionCategory;
  keywords?: string[];
  shortcut?: string; // e.g., cmd+k
  run: (ctx?: RegisteredActionContext) => Promise<void> | void;
}

export interface AgentEvent {
  type: 'agent:observe' | 'agent:act';
  source: string; // component id/name
  action?: string; // action id for agent:act
  payload?: unknown;
  timestamp: number;
}

type ActionMap = Map<string, RegisteredAction>;

class ActionRegistryImpl {
  private actions: ActionMap = new Map();
  private listeners: Set<(event: AgentEvent) => void> = new Set();

  register(action: RegisteredAction): void {
    this.actions.set(action.id, action);
  }

  unregister(actionId: string): void {
    this.actions.delete(actionId);
  }

  list(): RegisteredAction[] {
    return Array.from(this.actions.values());
  }

  get(actionId: string): RegisteredAction | undefined {
    return this.actions.get(actionId);
  }

  async run(actionId: string, ctx?: RegisteredActionContext): Promise<void> {
    const action = this.actions.get(actionId);
    if (!action) return;
    this.emit({ type: 'agent:act', source: 'ActionRegistry', action: actionId, timestamp: Date.now() });
    await action.run(ctx);
  }

  on(listener: (event: AgentEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  emit(event: AgentEvent): void {
    for (const l of this.listeners) {
      try {
        l(event);
      } catch {
        // ignore listener errors
      }
    }
  }
}

export const globalActionRegistry = new ActionRegistryImpl();

export function createAction(
  id: string,
  label: string,
  run: RegisteredAction['run'],
  opts?: Partial<Omit<RegisteredAction, 'id' | 'label' | 'run'>>
): RegisteredAction {
  return {
    id,
    label,
    category: opts?.category,
    keywords: opts?.keywords,
    shortcut: opts?.shortcut,
    run,
  };
}

// Convenience helpers for adapters to emit observe/act
export function emitObserve(source: string, payload?: unknown): void {
  globalActionRegistry.emit({ type: 'agent:observe', source, payload, timestamp: Date.now() });
}

export function emitAct(source: string, action: string, payload?: unknown): void {
  globalActionRegistry.emit({ type: 'agent:act', source, action, payload, timestamp: Date.now() });
}


